/**
 * Utility functions for auto-assigning onsite operations based on doctor and location
 */
import {
  Doctor,
  Location,
  MedicalAssistant,
  OnsiteOpsConfig,
  OpsGlobalSettings,
} from "~/api/API";

interface AutoAssignOnsiteOpParams {
  doctorId: string | null;
  locationId: string | null;
  allAssistants: MedicalAssistant[];
  allDoctors: Doctor[];
  allOnsiteOpsConfigs: OnsiteOpsConfig[];
}

interface AutoAssignResult {
  onsiteOpId: string | null;
  assignmentReason: "config" | "physician_partner" | "none";
  assignedMA: MedicalAssistant | null;
  isInactive: boolean;
}

/**
 * Determines the appropriate onsite op ID based on doctor and location
 * Uses the same logic as useAutoAssignOnsiteOps hook but in a synchronous utility function
 */
export const determineOnsiteOpId = ({
  doctorId,
  locationId,
  allAssistants,
  allDoctors,
  allOnsiteOpsConfigs,
}: AutoAssignOnsiteOpParams): AutoAssignResult => {
  // Return early if we don't have both doctor and location
  if (!doctorId || !locationId) {
    return {
      onsiteOpId: null,
      assignmentReason: "none",
      assignedMA: null,
      isInactive: false,
    };
  }

  // Look for explicit onsite ops configuration
  const compositeKey = `${doctorId}#${locationId}`;
  const config = allOnsiteOpsConfigs.find(
    (c) => c.doctorLocationKey === compositeKey,
  );

  let assignedMA: MedicalAssistant | null = null;
  let assignmentReason: "config" | "physician_partner" | "none" = "none";

  // Try configuration first
  if (config?.onsiteOpsConfigMedicalAssistantId) {
    assignedMA =
      allAssistants.find(
        (a) => a.id === config.onsiteOpsConfigMedicalAssistantId,
      ) || null;
    assignmentReason = "config";
  }

  // Fallback to doctor's physician partner if no config found
  if (!assignedMA) {
    const doctor = allDoctors.find((d) => d.id === doctorId);
    if (doctor?.physicianPartner) {
      assignedMA =
        allAssistants.find((a) => a.id === doctor.physicianPartner!.id) || null;
      assignmentReason = "physician_partner";
    }
  }

  // Return result
  if (!assignedMA) {
    return {
      onsiteOpId: null,
      assignmentReason: "none",
      assignedMA: null,
      isInactive: false,
    };
  }

  return {
    onsiteOpId: assignedMA.id,
    assignmentReason,
    assignedMA,
    isInactive: assignedMA.inactive || false,
  };
};

export interface DefaultOnsiteOpParams {
  doctorId: string | null;
  locationId: string | null;
  allAssistants: MedicalAssistant[];
  allDoctors: Doctor[];
  allLocations: Location[];
  globalSettings: OpsGlobalSettings | null;
}

/**
 * The first active default on-site op configured in Ops Settings: the
 * doctor's primary/secondary, then the location's default/secondary, then the
 * global default. Only fills an empty on-site op — a doctor+location config or
 * physician partner match (`determineOnsiteOpId`) and an op already on the
 * appointment both take precedence.
 */
export const determineDefaultOnsiteOp = ({
  doctorId,
  locationId,
  allAssistants,
  allDoctors,
  allLocations,
  globalSettings,
}: DefaultOnsiteOpParams): MedicalAssistant | null => {
  const doctor = allDoctors.find((d) => d.id === doctorId);
  const location = allLocations.find((l) => l.id === locationId);
  const candidates = [
    doctor?.doctorDefaultPrimaryOnsiteOpsId,
    doctor?.doctorDefaultSecondaryOnsiteOpsId,
    location?.locationDefaultOnsiteOpsId,
    location?.locationSecondaryOnsiteOpsId,
    globalSettings?.opsGlobalSettingsDefaultGlobalOnsiteOpsId,
  ];
  for (const candidate of candidates) {
    const assistant = allAssistants.find((a) => a.id === candidate);
    if (assistant && !assistant.inactive) return assistant;
  }
  return null;
};

/**
 * Gets the onsite op ID to use for appointment creation: the doctor+location
 * match, then the existing onsite op, then the Ops Settings defaults.
 */
export const getOnsiteOpIdForAppointment = (
  params: DefaultOnsiteOpParams & {
    allOnsiteOpsConfigs: OnsiteOpsConfig[];
  },
  existingOnsiteOpId?: string | null,
): string | null => {
  const result = determineOnsiteOpId(params);

  // If auto-assignment found an active MA, use it
  if (result.onsiteOpId && !result.isInactive) {
    return result.onsiteOpId;
  }

  if (existingOnsiteOpId) return existingOnsiteOpId;

  return determineDefaultOnsiteOp(params)?.id ?? null;
};
